import React, { useState } from 'react';
import { X, Upload, Save, Image as ImageIcon, AlertTriangle, Plus } from 'lucide-react';
import {
  criarBugReport,
  CategoriaBugReport,
  Criticidade,
} from '../../services/bugReportService';
import { CriticidadeBadge } from './CriticidadeBadge';
import { CategoriaModal } from './CategoriaModal';
import useAuthStore from '../../stores/authStore';

interface Props {
  categorias: CategoriaBugReport[];
  onClose: () => void;
  onCreated: () => void;
  /** Callback para recarregar categorias quando MASTER cria uma nova inline */
  onCategoriasChanged?: () => void;
}

const CRITICIDADES: Criticidade[] = ['BAIXA', 'MEDIA', 'ALTA', 'CRITICA'];

export const BugReportCreateModal: React.FC<Props> = ({ categorias, onClose, onCreated, onCategoriasChanged }) => {
  const user = useAuthStore((state: any) => state.user);
  const isMaster = user?.role === 'MASTER';

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [criticidade, setCriticidade] = useState<Criticidade>('MEDIA');
  const [categoriaId, setCategoriaId] = useState<number | ''>('');
  const [foto, setFoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [showNovaCategoriaModal, setShowNovaCategoriaModal] = useState(false);

  const categoriasAtivas = categorias.filter((c) => c.ativo);

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('Foto muito grande (max 5MB)');
        return;
      }
      setFoto(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (titulo.trim().length < 5) {
      alert('Titulo muito curto');
      return;
    }
    if (descricao.trim().length < 10) {
      alert('Descricao muito curta');
      return;
    }
    if (!categoriaId) {
      alert('Selecione uma categoria');
      return;
    }

    setSaving(true);
    try {
      const categoria = categorias.find((c) => c.id === categoriaId);
      await criarBugReport({
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        criticidade,
        categoria_id: categoriaId as number,
        categoria_nome: categoria?.nome,
        foto,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      console.error(err);
      alert('Erro ao criar: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-red-50">
          <h2 className="text-lg font-bold text-red-900">Nova Ocorrencia</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Categoria */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Categoria *</label>

            {categoriasAtivas.length === 0 ? (
              // Empty state defensivo: dropdown vazio (fetch falhou ou nenhuma cadastrada)
              <div className="border border-amber-300 bg-amber-50 p-3 rounded text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900">
                      Nenhuma categoria disponível.
                    </p>
                    {isMaster ? (
                      <>
                        <p className="text-amber-800 text-xs mt-1">
                          Como MASTER, você pode criar uma nova categoria agora.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowNovaCategoriaModal(true)}
                          className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
                        >
                          <Plus className="w-3.5 h-3.5" /> Criar nova categoria
                        </button>
                      </>
                    ) : (
                      <p className="text-amber-800 text-xs mt-1">
                        Solicite a um administrador (MASTER) que cadastre categorias em
                        {' '}<strong>Bug Reports → Categorias</strong>.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Dropdown normal — categorias disponíveis
              <>
                <select
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  value={categoriaId}
                  onChange={(e) => setCategoriaId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Selecione...</option>
                  {categoriasAtivas.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
                {isMaster && (
                  <button
                    type="button"
                    onClick={() => setShowNovaCategoriaModal(true)}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="w-3 h-3" /> Criar nova categoria
                  </button>
                )}
              </>
            )}
          </div>

          {/* Criticidade */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Criticidade *</label>
            <div className="flex gap-2">
              {CRITICIDADES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCriticidade(c)}
                  className={`flex-1 px-2 py-2 rounded border-2 transition-colors ${
                    criticidade === c
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CriticidadeBadge criticidade={c} />
                </button>
              ))}
            </div>
          </div>

          {/* Titulo */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Titulo *</label>
            <input
              type="text"
              required
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Resumo curto do problema"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
          </div>

          {/* Descricao */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Descricao detalhada *</label>
            <textarea
              required
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Descreva o que aconteceu, qual produto/tela, o que voce esperava..."
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>

          {/* Foto */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Foto/Evidencia (opcional)</label>
            <label className="flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50">
              {foto ? (
                <>
                  <ImageIcon className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">{foto.name}</span>
                  <span className="text-xs text-gray-500">({(foto.size / 1024).toFixed(0)}KB)</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Clique para anexar foto (max 5MB)</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFotoChange}
              />
            </label>
            {foto && (
              <button
                type="button"
                onClick={() => setFoto(null)}
                className="text-xs text-red-600 hover:text-red-800 mt-1"
              >
                Remover foto
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || categoriasAtivas.length === 0}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            title={
              categoriasAtivas.length === 0
                ? 'Cadastre uma categoria primeiro'
                : undefined
            }
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Registrar Ocorrencia'}
          </button>
        </div>
      </form>

      {/* Modal inline para MASTER cadastrar nova categoria sem sair do form */}
      {showNovaCategoriaModal && (
        <CategoriaModal
          categoria={null}
          onClose={() => setShowNovaCategoriaModal(false)}
          onSaved={() => {
            setShowNovaCategoriaModal(false);
            // Recarrega categorias do parent (BugReportsPage)
            onCategoriasChanged?.();
          }}
        />
      )}
    </div>
  );
};
